// src/components/AankondigingModal.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useDoorloopjes } from '../context/DoorloopjesContext';
import { getConsent, onConsentChosen } from '../utils/consent';
import { schoolPromptNodig } from './SchoolPromptModal';
import './AankondigingModal.css';

const STRAPI_URL = process.env.REACT_APP_STRAPI_URL;
const FOCUSBAAR = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

const LIJSTREGEL = /^- /;

// Platte tekst uit de admin: een lege regel scheidt blokken, opeenvolgende regels die met "- "
// beginnen worden een opsomming, andere regelovergangen blijven regelovergangen.
function Tekst({ tekst }) {
  return tekst
    .replace(/\r\n?/g, '\n')
    .split(/\n\s*\n/)
    .flatMap((blok, b) => {
      const groepen = [];
      for (const regel of blok.split('\n').filter((r) => r.trim())) {
        const lijst = LIJSTREGEL.test(regel);
        const inhoud = lijst ? regel.replace(LIJSTREGEL, '') : regel;
        const laatste = groepen[groepen.length - 1];
        if (laatste?.lijst === lijst) laatste.regels.push(inhoud);
        else groepen.push({ lijst, regels: [inhoud] });
      }
      return groepen.map(({ lijst, regels }, g) =>
        lijst ? (
          <ul key={`${b}-${g}`}>
            {regels.map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        ) : (
          <p key={`${b}-${g}`}>
            {regels.map((r, i) => (
              <React.Fragment key={i}>
                {i > 0 && <br />}
                {r}
              </React.Fragment>
            ))}
          </p>
        ),
      );
    });
}

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('jwt')}` } });

/**
 * Pop-up uit de admin (Aankondiging), één keer per gebruiker. Of hij er is, beslist de backend
 * (venster, sleutel al gezien, video-analyse). Hij komt pas als de tour, de schoolvraag, de
 * contactmodal en de cookiebanner weg zijn. Sluiten (kruisje, Esc) en de knop markeren hem als
 * gezien; een tik naast de pop-up niet, want dan zou hij voorgoed weg zijn zonder dat iemand hem las.
 */
function AankondigingModal({ andereModalOpen = false }) {
  const { currentUser, tourState } = useDoorloopjes();
  const [aankondiging, setAankondiging] = useState(null);
  const [cookieKeuzeOpen, setCookieKeuzeOpen] = useState(() => !getConsent());
  const [show, setShow] = useState(false); // voor de fade-in
  const dialoogRef = useRef(null);
  const userId = currentUser?.id;

  useEffect(() => onConsentChosen(() => setCookieKeuzeOpen(false)), []);

  useEffect(() => {
    if (!userId) return undefined;
    let actueel = true;
    axios
      .get(`${STRAPI_URL}/api/aankondiging`, auth())
      .then((res) => actueel && setAankondiging(res.data?.aankondiging ?? null))
      .catch(() => {}); // geen pop-up is de veilige kant
    return () => {
      actueel = false;
    };
  }, [userId]);

  const wacht =
    andereModalOpen || cookieKeuzeOpen || Boolean(tourState?.run) || schoolPromptNodig(currentUser);
  const open = Boolean(aankondiging) && !wacht;

  const sluit = useCallback(() => {
    setAankondiging(null);
    axios.post(`${STRAPI_URL}/api/aankondiging/gezien`, {}, auth()).catch(() => {});
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => setShow(true), 50);
    const vorige = document.activeElement;
    dialoogRef.current?.focus();

    const opToets = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        sluit();
        return;
      }
      if (e.key !== 'Tab' || !dialoogRef.current) return;
      const focusbaar = Array.from(dialoogRef.current.querySelectorAll(FOCUSBAAR));
      if (focusbaar.length === 0) return;
      const eerste = focusbaar[0];
      const laatste = focusbaar[focusbaar.length - 1];
      const binnen = dialoogRef.current.contains(document.activeElement);
      if (e.shiftKey && (!binnen || document.activeElement === eerste || document.activeElement === dialoogRef.current)) {
        e.preventDefault();
        laatste.focus();
      } else if (!e.shiftKey && (!binnen || document.activeElement === laatste)) {
        e.preventDefault();
        eerste.focus();
      }
    };
    document.addEventListener('keydown', opToets);
    return () => {
      clearTimeout(t);
      setShow(false);
      document.removeEventListener('keydown', opToets);
      if (vorige && typeof vorige.focus === 'function') vorige.focus();
    };
  }, [open, sluit]);

  if (!open) return null;

  return (
    <div className={`modal-backdrop aankondiging-backdrop ${show ? 'show' : ''}`}>
      <div
        ref={dialoogRef}
        className="modal-content aankondiging-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="aankondiging-titel"
        aria-describedby="aankondiging-tekst"
        tabIndex={-1}
      >
        <div className="modal-header">
          <h2 id="aankondiging-titel">{aankondiging.titel}</h2>
          <button type="button" className="modal-close-btn" onClick={sluit} aria-label="Sluiten">
            ×
          </button>
        </div>
        <div id="aankondiging-tekst" className="aankondiging-tekst">
          <Tekst tekst={aankondiging.tekst} />
        </div>
        <div className="aankondiging-knoppen">
          <a className="btn-primary" href={aankondiging.link} target="_blank" rel="noopener noreferrer" onClick={sluit}>
            {aankondiging.knoptekst}
          </a>
        </div>
      </div>
    </div>
  );
}

export default AankondigingModal;
